interface AgentStage {
  number: number;
  name: string;
  /**
   * Compact 1–2 word label rendered in the bar so 8-stage agents (UX) don't
   * overflow the viewport. Falls back to `name` when omitted; the full
   * `name` always appears in the title tooltip.
   */
  shortName?: string;
}

interface AgentProgressBarProps {
  stage: number;
  dimension: number;
  complete?: boolean;
  stages?: AgentStage[];
  totalDimensions?: number;
  staleStages?: number[];
  /**
   * #3708 — artefact-backed stages, keyed by stage number: true when that stage's artefact is
   * saved. A listed stage is ticked only when its artefact is saved, whatever the agent's own
   * stage/complete state says; an unlisted stage keeps the agent-state rule.
   */
  artefactStages?: Record<number, boolean>;
}

const DEFAULT_STAGES: AgentStage[] = [
  { number: 1, name: 'Idea Capture',    shortName: 'Idea' },
  { number: 2, name: 'Clarification',   shortName: 'Clarify' },
  { number: 3, name: 'PRD Generation',  shortName: 'PRD' },
  { number: 4, name: 'User Stories',    shortName: 'Stories' },
];

export default function AgentProgressBar({ stage: rawStage, dimension: rawDimension, complete = false, stages: propStages, totalDimensions = 6, staleStages, artefactStages }: AgentProgressBarProps) {
  const activeStages = propStages ?? DEFAULT_STAGES;
  const totalStages = activeStages.length;
  const stage = Math.min(totalStages, Math.max(1, rawStage));
  const dimension = Math.min(totalDimensions, Math.max(0, rawDimension));
  const staleSet = new Set(staleStages ?? []);
  const currentStage = activeStages.find(s => s.number === stage);

  return (
    <div className="flex-shrink-0 px-6 py-2" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
      {/* Mobile fallback — Step N of M: <full current stage name>.
          Hidden on >=sm so the multi-pill bar takes over. */}
      <div className="sm:hidden text-xs font-medium" style={{ color: 'var(--text)', fontFamily: 'var(--font-body)' }}>
        Step {stage} of {totalStages}
        {currentStage && (
          <>
            <span style={{ color: 'var(--text-secondary)' }}>: </span>
            <span style={{ color: 'var(--primary)' }}>{currentStage.name}</span>
          </>
        )}
      </div>

      <div className="hidden sm:flex items-center gap-1">
        {activeStages.map((s, i) => {
          const sNum = s.number;
          // #3708 — the agent reaching or finishing a stage is not enough when the stage has an
          // artefact: it is ticked only if that artefact is saved.
          const notSaved = artefactStages !== undefined && artefactStages[sNum] === false;
          const isCompleted = (stage > sNum || (stage === sNum && complete)) && !notSaved;
          const isCurrent = stage === sNum && !isCompleted;
          // A stage is stale if it's explicitly flagged OR it's completed and any earlier stage is stale
          const isStale = staleSet.has(sNum) || (isCompleted && activeStages.some(as => as.number < sNum && staleSet.has(as.number)));
          // Use shortName for the pill label; full name lives in the
          // title tooltip + the mobile fallback. When shortName isn't
          // supplied (older sessions), fall back to the full name.
          const label = s.shortName ?? s.name;

          return (
            <div key={sNum} className="flex items-center gap-1 flex-1 min-w-0">
              {/* Stage pill */}
              <div
                title={notSaved && stage >= sNum ? `${s.name} — not saved` : s.name}
                data-stage-state={isCompleted ? 'complete' : isCurrent ? 'current' : 'pending'}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors"
                style={
                  isStale && isCompleted
                    ? { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)' }
                    : isCompleted
                      ? { background: 'rgba(16,185,129,0.12)', color: 'var(--success)' }
                      : isCurrent
                        ? { background: 'rgba(124,58,237,0.10)', color: 'var(--primary)' }
                        : { background: 'var(--surface-alt)', color: 'var(--text-secondary)' }
                }
              >
                <span
                  className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 text-white"
                  style={
                    isStale && isCompleted
                      ? { background: 'var(--warning)' }
                      : isCompleted
                        ? { background: 'var(--success)' }
                        : isCurrent
                          ? { background: 'var(--primary)' }
                          : { background: 'var(--border)' }
                  }
                >
                  {isStale && isCompleted ? '!' : isCompleted ? '✓' : sNum}
                </span>
                <span>{label}</span>
                {sNum === 2 && isCurrent && dimension > 0 && (
                  <span style={{ color: 'var(--primary)', opacity: 0.7 }}>({dimension}/{totalDimensions})</span>
                )}
              </div>

              {/* Connector line — flex-1 so the line shrinks as the bar
                  fills with more pills (8-stage UX agent has shorter
                  connectors than the 3-stage Dev agent). */}
              {i < activeStages.length - 1 && (
                <div
                  className="flex-1 h-px mx-1 min-w-[8px]"
                  style={{
                    background: isStale && isCompleted
                      ? 'var(--warning)'
                      : isCompleted
                        ? 'var(--success)'
                        : 'var(--border)',
                    ...(isStale && isCompleted ? { backgroundImage: 'repeating-linear-gradient(90deg, var(--warning) 0, var(--warning) 4px, transparent 4px, transparent 8px)', background: 'none', height: 1 } : {}),
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {stage === 2 && dimension > 0 && (
        <p className="text-[11px] mt-1 text-center" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
          Asking dimension {dimension} of {totalDimensions}
        </p>
      )}
    </div>
  );
}
